import { Link } from "react-router-dom";
import { ArrowUpRight, CheckCircle } from "@phosphor-icons/react";
import { PageHeader } from "../components/PageHeader";
import { FadeUp, Overline } from "../components/Reveal";

const PILLARS = [
  {
    title: "Karma Sanyas",
    text: "Built by a yogi in the spirit of Karma Sanyas — work offered as selfless service to society, not as a means to extract.",
  },
  {
    title: "No investor pressure",
    text: "No boardroom mandate to squeeze clients and customers for the next quarter. Our only pressure is to serve patients, partners and the ecosystem well.",
  },
  {
    title: "Vasudhaiva Kutumbakam",
    text: "The world is one family. Technology should unite us and lift care for everyone — not divide, gatekeep or extract from it.",
  },
  {
    title: "Patients first",
    text: "More power to patients, better outcomes, and dignity in care — that is the point of everything we build.",
  },
];

const MANTRA = [
  "ॐ सर्वेषां स्वस्तिर्भवतु ।",
  "सर्वेषां शान्तिर्भवतु ।",
  "सर्वेषां पूर्णं भवतु ।",
  "सर्वेषां मङ्गलं भवतु ।",
  "ॐ शान्तिः शान्तिः शान्तिः ॥",
];

export default function Story() {
  return (
    <>
      <PageHeader
        testId="story-page"
        overline="Our Story"
        title={["A different kind", "of health company."]}
        subtitle="Anxya was born not in a boardroom, but from a conviction: healthcare's data and dignity belong to people — not to platforms that rent them back to us."
      />

      <section className="pb-24 lg:pb-32" data-testid="story-origin">
        <div className="mx-auto max-w-4xl px-6 lg:px-10">
          <FadeUp>
            <Overline>Where Anxya began</Overline>
            <div className="mt-8 space-y-6 text-base sm:text-lg text-muted-foreground leading-relaxed">
              <p>
                Anxya Tech and Anxya Health were built by a yogi — with a number of bots and AI agents, with love and deep connection — in Karma Sanyas: selfless service to society with renunciation of worldly excess. The purpose was simple: build technology as an offering, not as an instrument of extraction.
              </p>
              <p>
                Because of that, there is no investor or board-of-directors pressure to squeeze clients and customers for the next quarter. We are free to make decisions that are right for patients, practitioners, payers, providers and regulators — the entire healthcare ecosystem — over the long run, even when they are not the most profitable ones.
              </p>
              <p>
                At the heart of it is a belief we hold deeply — <span className="font-semibold text-primary">Vasudhaiva Kutumbakam</span>, “the world is one family.” Our conviction is that the world is genuinely one; it is the matrix of systems, incentives and gatekeepers that divides us. Anxya exists to soften those divisions in healthcare.
              </p>
              <p>
                We are among the very few trying to build a decentralised, democratic, distributed and data-sovereign healthcare system — and we would love for you to build it with us.
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-white border-y border-primary/10" data-testid="story-pillars">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <Overline>The principles we work by</Overline>
            <h2 className="mt-4 max-w-3xl font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight text-primary">
              Service, sovereignty and one human family
            </h2>
          </FadeUp>
          <div className="mt-14 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            {PILLARS.map((pillar, i) => (
              <FadeUp key={pillar.title} delay={i * 0.06}>
                <div className="h-full rounded-2xl border border-primary/10 bg-background p-7" data-testid={`story-pillar-${pillar.title.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
                  <CheckCircle size={24} weight="fill" className="text-accent" />
                  <h3 className="mt-6 font-display text-xl font-medium tracking-tight text-primary">{pillar.title}</h3>
                  <p className="mt-3 text-sm text-muted-foreground leading-relaxed">{pillar.text}</p>
                </div>
              </FadeUp>
            ))}
          </div>
        </div>
      </section>

      <section className="py-24 lg:py-32 bg-primary text-white" data-testid="story-mantra">
        <div className="mx-auto max-w-5xl px-6 lg:px-10 text-center">
          <FadeUp>
            <Overline>Sarvesham Svastir Bhavatu · Shanti Mantra</Overline>
            <h2 className="mt-4 font-display text-3xl sm:text-4xl lg:text-5xl font-medium tracking-tight">
              A prayer for the health, peace and fulfilment of all creation
            </h2>
          </FadeUp>
          <FadeUp delay={0.15}>
            <div className="mt-12 rounded-3xl border border-white/15 bg-white/5 p-8 lg:p-12">
              <div className="space-y-3 font-display text-2xl sm:text-3xl font-light leading-relaxed">
                {MANTRA.map((line) => <p key={line}>{line}</p>)}
              </div>
              <p className="mt-8 text-sm sm:text-base text-white/60 leading-relaxed">
                Om Sarveṣām Svastir Bhavatu | Sarveṣām Śāntir Bhavatu | Sarveṣām Pūrṇam Bhavatu | Sarveṣām Maṅgalam Bhavatu | Om Śāntiḥ Śāntiḥ Śāntiḥ
              </p>
              <p className="mt-6 text-base sm:text-lg text-white/80 leading-relaxed">
                “May all be blessed with well-being. May all attain peace. May all attain wholeness. May all be prosperous and happy. Om peace, peace, peace.”
              </p>
            </div>
          </FadeUp>
        </div>
      </section>

      <section className="py-24 lg:py-32" data-testid="story-cta">
        <div className="mx-auto max-w-7xl px-6 lg:px-10">
          <FadeUp>
            <div className="rounded-3xl border border-primary/10 bg-white p-10 lg:p-16 flex flex-col lg:flex-row items-start lg:items-center justify-between gap-10">
              <div className="max-w-2xl">
                <Overline>Build the future of care with us</Overline>
                <h2 className="mt-4 font-display text-3xl sm:text-4xl font-medium tracking-tight text-primary">
                  A data-sovereign, patient-first health platform — compliance-aligned and owned by you.
                </h2>
              </div>
              <div className="flex flex-wrap gap-4">
                <Link
                  to="/products/geneiux"
                  data-testid="story-platform-btn"
                  className="inline-flex items-center gap-2 rounded-full bg-primary px-7 py-3.5 text-sm font-semibold text-white transition-all duration-300 hover:-translate-y-0.5 hover:shadow-lg hover:shadow-accent/20"
                >
                  Explore Anxya AI OS <ArrowUpRight size={16} weight="bold" />
                </Link>
                <Link
                  to="/why-anxya"
                  data-testid="story-why-btn"
                  className="inline-flex items-center gap-2 rounded-full border border-primary/20 px-7 py-3.5 text-sm font-semibold text-primary transition-all duration-300 hover:-translate-y-0.5 hover:border-accent"
                >
                  Why Anxya
                </Link>
              </div>
            </div>
          </FadeUp>
        </div>
      </section>
    </>
  );
}
